import { call, put, select } from 'redux-saga/effects'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { selectExtraRoutesAuthData } from '@/containers/Login/AuthSelectors'
import {
  selectActiveAddress,
  selectCurrentAddressData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { saveAddress } from '@/containers/SubscriptionFormPageNew/Components/Address/AddressModuleSagas'
import {
  updateElectricityMeterAddressData,
  updateGasMeterAddressData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { t } from 'i18next'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import * as Sentry from '@sentry/react'

const createMeterSavePayload = (addressId, meterData, meterType) => {
  const isUpdate = !!meterData.id

  return {
    addressId,
    meterData: {
      meterType,
      ...(meterType === 'electricity' && {
        electricityMeter: {
          ...(isUpdate && { id: meterData.id }),
          pdlNo: meterData.pdlNo,
          pdlEligibilityData: meterData.pdlEligibilityData,
          oldOccupant: meterData.oldOccupant,
          consentDate: meterData.consentDate,
        },
      }),
      ...(meterType === 'gas' && {
        gasMeter: {
          ...(isUpdate && { id: meterData.id }),
          pceNo: meterData.pceNo,
          pceEligibilityData: meterData.pceEligibilityData,
          isValidPce: meterData.isValidPce,
          consentDate: meterData.consentDate,
        },
      }),
    },
  }
}

export function* saveMeterShared({ enqueue, meterData, addressId, meterType }) {
  const { saveAddressPodUrl } = yield select(selectExtraRoutesAuthData)
  const activeAddress = yield select(selectActiveAddress)

  if (!saveAddressPodUrl) {
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('saveAddressPodUrl endpoint not defined in auth data'))
    }
    return
  }

  const updateAction = meterType === 'electricity' ? updateElectricityMeterAddressData : updateGasMeterAddressData

  try {
    yield put(
      updateAction({
        data: { isSaving: true },
        id: activeAddress,
      }),
    )

    let finalAddressId = addressId

    if (parseInt(activeAddress) <= 0 || activeAddress === 'new') {
      try {
        yield call(saveAddress)
        const updatedAddressData = yield select(selectCurrentAddressData)
        finalAddressId = updatedAddressData.id

        if (!finalAddressId) {
          throw new Error('Address save failed - no address ID returned')
        }

        if (enqueue) {
          yield call(showSnackbar, enqueue, {
            type: SNACKBAR_TYPES.SUCCESS,
            message: t('subscriptionForm:snackbar.meter_save.address_updated'),
          })
        }
      } catch (addressSaveError) {
        if (import.meta.env.PROD) {
          Sentry.captureException(new Error('Address save failed during meter save process'), {
            extra: { error: addressSaveError },
          })
        }
        if (enqueue) {
          yield call(showSnackbar, enqueue, {
            type: SNACKBAR_TYPES.ERROR,
            message: t('subscriptionForm:snackbar.meter_save.save_error'),
          })
        }
        return
      }
    }

    const apiPayload = createMeterSavePayload(finalAddressId, meterData, meterType)

    const response = yield call(callApi, {
      endpoint: saveAddressPodUrl,
      method: 'POST',
      payload: apiPayload,
      showErrorSnackbar: true,
    })

    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message: t('subscriptionForm:snackbar.meter_save.meter_saved'),
      })
    }

    return response
  } catch (error) {
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(new Error(`save${meterType}MeterError`), { extra: { error } })
    }

    if (enqueue) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: t('subscriptionForm:snackbar.meter_save.save_error'),
      })
    }
  } finally {
    yield put(
      updateAction({
        data: { isSaving: false },
        id: activeAddress,
      }),
    )
  }
}
